import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listActiveStudents, listFrequencies } from "../services/student";
import {
  bulkProcessPayments,
  downloadBulkReceipt,
  listEmoluments,
  listStudentExtraconfigs,
  payGroup,
} from "../services/payment";
import { getUserInfo } from "../services/auth";
import { listPaymentTypes } from "../services/paymentTypes";
import {
  buildPropinaRows,
  effectiveCarierContact,
  formatCurrencyMZN,
  monthShortName,
  PAYMENT_METHODS,
} from "../utils/paymentHelpers";

// Ported from the (unmerged) navy_blue_sky branch of sge_infantil_web,
// src/pages/payment/bulk/index.js — "Facturação Agregada". Two backend flows
// depending on how many students are selected:
//   - 1 student  → bulkProcessPayments: N propinas (+ optional taxes/extras/
//     discount) from that ONE student, processed as a batch.
//   - 2+ students → payGroup: propinas of DIFFERENT students that share the
//     SAME guardian contact, splitting one (possibly partial) amountPaid
//     proportionally — no discount/taxes/extras support (backend v1 scope).
const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);
const todayIso = () => new Date().toISOString().split("T")[0];

const taxValueOf = (tax) => parseFloat(tax?.taxValue ?? tax?.amount ?? tax?.value ?? 0) || 0;
const taxNameOf = (tax) => tax?.description || tax?.name || tax?.taxName || "-";

const FacturacaoAgregadaLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [filterYear, setFilterYear] = useState(currentYear);
  const [registrations, setRegistrations] = useState([]);
  const [extraconfigsData, setExtraconfigsData] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [availableTaxes, setAvailableTaxes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [studentFilter, setStudentFilter] = useState("");
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [selectedTaxes, setSelectedTaxes] = useState([]);
  const [payOnlyPropina, setPayOnlyPropina] = useState(false);

  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState(0);
  const [discountScope, setDiscountScope] = useState("propinas");

  const [paymentMethod, setPaymentMethod] = useState("Numerário");
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [amountPaid, setAmountPaid] = useState("");
  const [paymentMethods, setPaymentMethods] = useState(PAYMENT_METHODS);

  // Tipos de Pagamento por sucursal (SGE_PAYMENT_TYPES) - PAYMENT_METHODS só
  // é usado como reserva se a chamada falhar ou a sucursal não tiver tipos.
  useEffect(() => {
    listPaymentTypes()
      .then((resp) => {
        const names = (resp.data || []).map((pt) => pt.name).filter(Boolean);
        if (names.length) setPaymentMethods(names);
      })
      .catch(() => {});
  }, []);

  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentResponse, setPaymentResponse] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchData = (year) => {
    setLoading(true);
    Promise.all([listActiveStudents(year), listStudentExtraconfigs()])
      .then(([regResp, extraResp]) => {
        setRegistrations(regResp.data || []);
        const extras = (extraResp.data || []).filter((item) => Number(item.year) === Number(year));
        setExtraconfigsData(extras);
      })
      .catch(() => {
        setRegistrations([]);
        setExtraconfigsData([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData(filterYear);
    setSelectedStudentIds([]);
    setSelectedPayments([]);
    setSelectedTaxes([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterYear]);

  useEffect(() => {
    listFrequencies()
      .then((resp) => setFrequencies(resp.data || []))
      .catch(() => setFrequencies([]));
    listEmoluments()
      .then((resp) => {
        const taxes = (resp.data || []).filter((tx) => tx && (String(tx.active) === "1" || tx.active === 1));
        setAvailableTaxes(Array.from(new Map(taxes.map((tx) => [tx.id, tx])).values()));
      })
      .catch(() => setAvailableTaxes([]));
  }, []);

  const rows = useMemo(
    () => buildPropinaRows(registrations, extraconfigsData, frequencies, filterYear),
    [registrations, extraconfigsData, frequencies, filterYear],
  );

  const studentOptions = useMemo(() => {
    const term = studentFilter.trim().toLowerCase();
    return rows.filter(
      (r) => !term || r.name?.toLowerCase().includes(term) || r.code?.toLowerCase().includes(term),
    );
  }, [rows, studentFilter]);

  const selectedRows = useMemo(
    () => selectedStudentIds.map((id) => rows.find((r) => r.registrationId === id)).filter(Boolean),
    [selectedStudentIds, rows],
  );
  const groupMode = selectedStudentIds.length > 1;

  const handleToggleStudent = (registrationId) => {
    const isSelected = selectedStudentIds.includes(registrationId);
    const next = isSelected
      ? selectedStudentIds.filter((id) => id !== registrationId)
      : [...selectedStudentIds, registrationId];

    if (next.length > 1) {
      const nextRows = next.map((id) => rows.find((r) => r.registrationId === id)).filter(Boolean);
      const contacts = new Set(nextRows.map(effectiveCarierContact));
      if (contacts.size > 1 || contacts.has("")) {
        toast.error(t("bulk.error.carierMismatch"));
        return;
      }
    }
    setSelectedStudentIds(next);
    setSelectedPayments([]);
    setSelectedTaxes([]);
    setAmountPaid("");
    setPayOnlyPropina(false);
  };

  // ── Single-student mode ──────────────────────────────────────────────
  const singlePayments = useMemo(() => {
    if (groupMode || selectedRows.length !== 1) return [];
    return selectedRows[0].payments.filter((p) => Number(p.originalStatus) === 0);
  }, [selectedRows, groupMode]);

  const togglePayment = (id) =>
    setSelectedPayments((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const selectAllSinglePayments = () => {
    const ids = singlePayments.map((p) => p.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedPayments.includes(id));
    setSelectedPayments(allSelected ? [] : ids);
  };

  const toggleTax = (tax) => {
    const registrationId = selectedRows[0]?.registrationId;
    if (!registrationId) return;
    setSelectedTaxes((cur) => {
      const exists = cur.some((t) => t.taxId === tax.id);
      return exists
        ? cur.filter((t) => t.taxId !== tax.id)
        : [...cur, { taxId: tax.id, registrationId, amountPaid: taxValueOf(tax) }];
    });
  };

  const selectAllTaxes = () => {
    const registrationId = selectedRows[0]?.registrationId;
    if (!registrationId) return;
    const allSelected =
      availableTaxes.length > 0 && availableTaxes.every((tx) => selectedTaxes.some((t) => t.taxId === tx.id));
    setSelectedTaxes(
      allSelected
        ? []
        : availableTaxes.map((tx) => ({ taxId: tx.id, registrationId, amountPaid: taxValueOf(tx) })),
    );
  };

  // Editable "Valor a Pagar" per selected tax — allows paying only part of a
  // tax's value. Clamped to [0, valor cheio da taxa].
  const setTaxAmount = (taxId, value) => {
    const tax = availableTaxes.find((tx) => tx.id === taxId);
    const max = taxValueOf(tax);
    const parsed = value === "" ? "" : Math.max(0, Math.min(parseFloat(value) || 0, max));
    setSelectedTaxes((cur) => cur.map((t) => (t.taxId === taxId ? { ...t, amountPaid: parsed } : t)));
  };

  const selectedPaymentObjects = useMemo(
    () => singlePayments.filter((p) => selectedPayments.includes(p.id)),
    [singlePayments, selectedPayments],
  );
  const hasExtrasInSelection = selectedPaymentObjects.some((p) => (p.extrasUnpaid || 0) > 0);
  const extrasTotal = useMemo(
    () => (payOnlyPropina ? 0 : selectedPaymentObjects.reduce((sum, p) => sum + (p.extrasUnpaid || 0), 0)),
    [selectedPaymentObjects, payOnlyPropina],
  );
  const extrasToPayList = useMemo(() => {
    if (payOnlyPropina) return [];
    const list = [];
    selectedPaymentObjects.forEach((p) =>
      (p.extrasForMonth || []).forEach((e) => {
        if (Number(e.status) === 0) {
          list.push({
            reference: e.reference || "",
            extraId: e.extraId,
            description: e.taxName || "",
            monthlyAmount: e.total || e.recurringAmount || 0,
            month: e.month,
          });
        }
      }),
    );
    return list;
  }, [selectedPaymentObjects, payOnlyPropina]);

  const propinasTotal = useMemo(
    () =>
      selectedPaymentObjects.reduce(
        (sum, p) => sum + (parseFloat(p.total) || 0) + (Number(p.hasFine) === 1 ? parseFloat(p.fine || 0) : 0),
        0,
      ),
    [selectedPaymentObjects],
  );
  const taxesTotal = useMemo(
    () => selectedTaxes.reduce((sum, t) => sum + (parseFloat(t.amountPaid) || 0), 0),
    [selectedTaxes],
  );
  // A tax paid for less than its full value is excluded from the discount base —
  // discount only applies to taxes being settled in full in this transaction.
  const taxesDiscountEligibleTotal = useMemo(
    () =>
      selectedTaxes.reduce((sum, t) => {
        const full = taxValueOf(availableTaxes.find((tx) => tx.id === t.taxId));
        const paid = parseFloat(t.amountPaid) || 0;
        return paid >= full - 0.01 ? sum + full : sum;
      }, 0),
    [selectedTaxes, availableTaxes],
  );
  const totalOriginal = propinasTotal + taxesTotal + extrasTotal;

  const availableScopes = useMemo(() => {
    const scopes = [{ value: "propinas", key: "bulk.discount.scope.propinas" }];
    if (selectedTaxes.length > 0) scopes.push({ value: "taxes", key: "bulk.discount.scope.taxes" });
    if (hasExtrasInSelection && !payOnlyPropina)
      scopes.push({ value: "propinas+extras", key: "bulk.discount.scope.propinasExtras" });
    if (selectedTaxes.length > 0) scopes.push({ value: "propinas+taxes", key: "bulk.discount.scope.propinasTaxes" });
    if (selectedTaxes.length > 0 && hasExtrasInSelection && !payOnlyPropina)
      scopes.push({ value: "propinas+extras+taxes", key: "bulk.discount.scope.all" });
    return scopes;
  }, [selectedTaxes, hasExtrasInSelection, payOnlyPropina]);

  useEffect(() => {
    if (!availableScopes.some((s) => s.value === discountScope)) setDiscountScope("propinas");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableScopes]);

  const baseAmountForScope = () => {
    switch (discountScope) {
      case "taxes":
        return taxesDiscountEligibleTotal;
      case "propinas+extras":
        return propinasTotal + extrasTotal;
      case "propinas+taxes":
        return propinasTotal + taxesDiscountEligibleTotal;
      case "propinas+extras+taxes":
        return propinasTotal + extrasTotal + taxesDiscountEligibleTotal;
      default:
        return propinasTotal;
    }
  };
  const discountAmount = useMemo(() => {
    if (!discountValue || discountValue <= 0) return 0;
    const base = baseAmountForScope();
    if (discountType === "percentage") return (base * discountValue) / 100;
    return Math.min(discountValue, base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountValue, discountType, discountScope, propinasTotal, extrasTotal, taxesDiscountEligibleTotal]);
  const finalTotal = totalOriginal - discountAmount;

  // ── Group mode ────────────────────────────────────────────────────────
  const groupFlatPayments = useMemo(() => {
    if (!groupMode) return [];
    return selectedRows.flatMap((row) =>
      row.payments
        .filter((p) => Number(p.originalStatus) === 0)
        .map((p) => ({ ...p, registrationId: row.registrationId, studentName: row.name })),
    );
  }, [selectedRows, groupMode]);

  const groupedByMonth = useMemo(() => {
    const map = new Map();
    groupFlatPayments.forEach((p) => {
      const key = `${p.month}-${p.year}`;
      if (!map.has(key)) {
        map.set(key, { key, month: p.month, year: p.year, total: 0, fine: 0, studentNames: [], paymentIds: [] });
      }
      const bucket = map.get(key);
      bucket.total += parseFloat(p.total) || 0;
      bucket.fine += Number(p.hasFine) === 1 ? parseFloat(p.fine || 0) : 0;
      if (!bucket.studentNames.includes(p.studentName)) bucket.studentNames.push(p.studentName);
      bucket.paymentIds.push(p.id);
    });
    return Array.from(map.values()).sort(
      (a, b) => Number(a.year) - Number(b.year) || Number(a.month) - Number(b.month),
    );
  }, [groupFlatPayments]);

  const isBucketSelected = (bucket) => bucket.paymentIds.every((id) => selectedPayments.includes(id));
  const toggleBucket = (bucket) => {
    const allSelected = isBucketSelected(bucket);
    setSelectedPayments((cur) =>
      allSelected
        ? cur.filter((id) => !bucket.paymentIds.includes(id))
        : Array.from(new Set([...cur, ...bucket.paymentIds])),
    );
  };
  const selectAllBuckets = () => {
    const allIds = groupedByMonth.flatMap((b) => b.paymentIds);
    const allSelected = allIds.length > 0 && allIds.every((id) => selectedPayments.includes(id));
    setSelectedPayments(allSelected ? [] : allIds);
  };

  const groupSelectedTotal = useMemo(
    () =>
      selectedPayments.reduce((sum, id) => {
        const p = groupFlatPayments.find((pp) => pp.id === id);
        if (!p) return sum;
        return sum + (parseFloat(p.total) || 0) + (Number(p.hasFine) === 1 ? parseFloat(p.fine || 0) : 0);
      }, 0),
    [selectedPayments, groupFlatPayments],
  );
  const groupAmountPaid = useMemo(() => {
    const max = groupSelectedTotal;
    const val = amountPaid === "" ? max : Math.min(parseFloat(amountPaid) || 0, max);
    return Math.max(0, val);
  }, [amountPaid, groupSelectedTotal]);
  const groupRemaining = Math.max(0, groupSelectedTotal - groupAmountPaid);

  // ── Submission ────────────────────────────────────────────────────────
  const handleOpenConfirm = () => {
    if (groupMode) {
      if (selectedPayments.length < 2) {
        toast.error(t("bulk.error.groupMinItems"));
        return;
      }
      if (!(groupAmountPaid > 0)) {
        toast.error(t("bulk.error.groupAmountRequired"));
        return;
      }
    } else if (selectedPayments.length === 0 && selectedTaxes.length === 0) {
      toast.error(t("bulk.error.noItems"));
      return;
    }
    if (!paymentMethod) {
      toast.error(t("propina.amortize.error.method"));
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setProcessingPayment(true);
    try {
      if (groupMode) {
        const payload = {
          payments: selectedPayments.map((id) => {
            const p = groupFlatPayments.find((pp) => pp.id === id);
            return { paymentId: id, registrationId: p?.registrationId };
          }),
          amountPaid: groupAmountPaid,
          paymentMethod,
          updatedBy: getUserInfo()?.id,
          paymentDate,
        };
        const resp = await payGroup(payload);
        setPaymentResponse(resp.data);
      } else {
        const payload = {
          paymentIds: selectedPayments,
          paymentMethod,
          updatedBy: getUserInfo()?.id,
          paymentDate,
        };
        if (discountValue > 0) {
          payload.discount = { value: discountValue, isPercentage: discountType === "percentage", scope: discountScope };
        }
        if (selectedTaxes.length > 0) {
          payload.taxes = selectedTaxes.map(({ taxId, registrationId, amountPaid: taxAmountPaid }) => ({
            taxId,
            registrationId,
            amountPaid: taxAmountPaid,
          }));
        }
        if (extrasToPayList.length > 0) payload.extras = extrasToPayList;
        const resp = await bulkProcessPayments(payload);
        setPaymentResponse(resp.data);
      }
      setPaymentSuccess(true);
      setConfirmOpen(false);
      fetchData(filterYear);
    } catch (err) {
      toast.error(t("propina.error.generic"));
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleRestart = () => {
    setSelectedStudentIds([]);
    setSelectedPayments([]);
    setSelectedTaxes([]);
    setPayOnlyPropina(false);
    setDiscountType("percentage");
    setDiscountValue(0);
    setDiscountScope("propinas");
    setPaymentMethod("Numerário");
    setPaymentDate(todayIso());
    setAmountPaid("");
    setPaymentSuccess(false);
    setPaymentResponse(null);
    setStudentFilter("");
  };

  const handlePrintReceipt = async () => {
    if (groupMode) {
      if (paymentResponse?.pdf) {
        navigate("/documentos/pdf", {
          state: { url: paymentResponse.pdf, title: t("bulk.receiptTitle"), filename: "factura-agregada.pdf" },
        });
      } else {
        toast.error(t("propina.error.print"));
      }
      return;
    }
    const registrationId = selectedRows[0]?.registrationId;
    if (!registrationId) return;
    try {
      const paymentIds = (paymentResponse?.details?.successful || []).map((d) => d.paymentId);
      const taxIds = (paymentResponse?.details?.taxPaymentsCreated || []).map((d) => d.taxPaymentId);
      const resp = await downloadBulkReceipt(registrationId, { paymentIds, taxIds, extras: extrasToPayList });
      if (resp.data?.pdf) {
        navigate("/documentos/pdf", {
          state: { url: resp.data.pdf, title: t("bulk.receiptTitle"), filename: "factura-agregada.pdf" },
        });
      } else {
        toast.error(t("propina.error.print"));
      }
    } catch (err) {
      toast.error(t("propina.error.print"));
    }
  };

  // ── Render ───────────────────────────────────────────────────────────
  if (paymentSuccess) {
    const summary = paymentResponse?.summary;
    const details = paymentResponse?.details;
    return (
      <div className='row gy-4'>
        <ToastContainer />
        <div className='col-12'>
          <div className='card radius-12'>
            <div className='card-body text-center py-40'>
              <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                <i className='ri-check-line' />
              </span>
              <h6 className='text-md text-neutral-600'>{t("bulk.success.title")}</h6>
              <p className='text-neutral-400 text-sm mb-24'>
                {groupMode ? t("bulk.success.subtitleGroup") : t("bulk.success.subtitle")}
              </p>

              {!groupMode && summary && (
                <div className='row gy-3 justify-content-center mb-24'>
                  <div className='col-sm-3'>
                    <div className='p-16 radius-8 bg-primary-50 text-center'>
                      <span className='text-secondary-light text-sm d-block'>{t("bulk.summary.totalProcessed")}</span>
                      <strong className='text-primary-600 text-xl'>{summary.totalProcessed}</strong>
                    </div>
                  </div>
                  <div className='col-sm-3'>
                    <div className='p-16 radius-8 bg-success-focus text-center'>
                      <span className='text-secondary-light text-sm d-block'>{t("bulk.summary.successful")}</span>
                      <strong className='text-success-main text-xl'>{summary.successful}</strong>
                    </div>
                  </div>
                  {summary.failed > 0 && (
                    <div className='col-sm-3'>
                      <div className='p-16 radius-8 bg-danger-focus text-center'>
                        <span className='text-secondary-light text-sm d-block'>{t("bulk.summary.failed")}</span>
                        <strong className='text-danger-main text-xl'>{summary.failed}</strong>
                      </div>
                    </div>
                  )}
                  {summary.taxPaymentsCreated > 0 && (
                    <div className='col-sm-3'>
                      <div className='p-16 radius-8 bg-warning-focus text-center'>
                        <span className='text-secondary-light text-sm d-block'>{t("bulk.summary.taxesCreated")}</span>
                        <strong className='text-warning-main text-xl'>{summary.taxPaymentsCreated}</strong>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!groupMode && Array.isArray(details?.taxPaymentsCreated) && details.taxPaymentsCreated.length > 0 && (
                <div className='table-responsive mb-24'>
                  <table className='table table-bordered table-sm mb-0 bg-base'>
                    <thead>
                      <tr>
                        <th className='bg-neutral-50'>{t("bulk.col.taxName")}</th>
                        <th className='bg-neutral-50'>{t("propina.col.total")}</th>
                        <th className='bg-neutral-50'>{t("bulk.col.paidAmount")}</th>
                        <th className='bg-neutral-50'>{t("bulk.col.remaining")}</th>
                        <th className='bg-neutral-50'>{t("global.status")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.taxPaymentsCreated.map((d) => {
                        const isPartial = Number(d.status) === 2;
                        return (
                          <tr key={d.taxPaymentId}>
                            <td>{taxNameOf(availableTaxes.find((tx) => tx.id === d.taxId))}</td>
                            <td>{formatCurrencyMZN(d.finalValue)}</td>
                            <td>{formatCurrencyMZN(d.amountPaid)}</td>
                            <td>{d.outstandingAmount > 0.01 ? formatCurrencyMZN(d.outstandingAmount) : "-"}</td>
                            <td>
                              <span className={`px-8 py-2 radius-4 text-xs fw-medium ${isPartial ? "bg-warning-focus text-warning-main" : "bg-success-focus text-success-main"}`}>
                                {isPartial ? t("propina.status.partial") : t("propina.status.paid")}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {groupMode && Array.isArray(details) && (
                <div className='table-responsive mb-24'>
                  <table className='table table-bordered table-sm mb-0 bg-base'>
                    <thead>
                      <tr>
                        <th className='bg-neutral-50'>{t("global.name")}</th>
                        <th className='bg-neutral-50'>{t("propina.col.total")}</th>
                        <th className='bg-neutral-50'>{t("bulk.col.paidAmount")}</th>
                        <th className='bg-neutral-50'>{t("bulk.col.remaining")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.map((d) => (
                        <tr key={d.paymentId}>
                          <td>{d.studentName}</td>
                          <td>{formatCurrencyMZN(d.total)}</td>
                          <td>{formatCurrencyMZN(d.paidAmount)}</td>
                          <td>{d.remaining > 0.01 ? formatCurrencyMZN(d.remaining) : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className='d-flex align-items-center justify-content-center gap-8'>
                <button type='button' className='btn btn-outline-primary-600 px-32' onClick={handlePrintReceipt}>
                  {t("propina.action.receipt")}
                </button>
                <button type='button' className='btn btn-neutral-500 border-neutral-100 px-32' onClick={handleRestart}>
                  {t("bulk.action.newPayment")}
                </button>
                <button
                  type='button'
                  className='btn btn-primary-600 px-32'
                  onClick={() => navigate("/pagamentos/propinas")}
                >
                  <i className='ri-arrow-left-line' /> {t("propina.action.backToPayments")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='row gy-4'>
      <ToastContainer />
      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <h6 className='mb-4'>{t("bulk.title")}</h6>
            <p className='text-secondary-light mb-24 text-sm'>{t("bulk.subtitle")}</p>

            <div className='row gy-3 mb-16'>
              <div className='col-sm-4 col-lg-2'>
                <label className='form-label text-sm mb-8'>{t("estudante.field.selectedYear")}</label>
                <select
                  className='form-control form-select'
                  value={filterYear}
                  onChange={(e) => setFilterYear(Number(e.target.value))}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-sm-8 col-lg-5'>
                <label className='form-label text-sm mb-8'>{t("bulk.field.searchStudent")}</label>
                <div className='icon-field'>
                  <span className='icon top-50 translate-middle-y'>
                    <Icon icon='ion:search-outline' />
                  </span>
                  <input
                    type='text'
                    className='form-control'
                    placeholder={t("bulk.field.searchStudent.placeholder")}
                    value={studentFilter}
                    onChange={(e) => setStudentFilter(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {loading ? (
              <div className='text-center text-secondary-light py-32'>{t("estudante.list.loading")}</div>
            ) : (
              <>
                <label className='form-label text-sm mb-8'>{t("bulk.field.selectStudents")}</label>
                <div className='table-responsive mb-8' style={{ maxHeight: 220, overflowY: "auto" }}>
                  <table className='table table-bordered table-sm mb-0 bg-base'>
                    <tbody>
                      {studentOptions.length === 0 && (
                        <tr>
                          <td className='text-center text-secondary-light py-16'>{t("estudante.list.empty")}</td>
                        </tr>
                      )}
                      {studentOptions.map((row) => {
                        const checked = selectedStudentIds.includes(row.registrationId);
                        return (
                          <tr
                            key={row.registrationId}
                            role='button'
                            className={checked ? "bg-primary-50" : ""}
                            onClick={() => handleToggleStudent(row.registrationId)}
                          >
                            <td style={{ width: 36 }}>
                              <input
                                type='checkbox'
                                className='form-check-input'
                                checked={checked}
                                onChange={() => handleToggleStudent(row.registrationId)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td className='fw-semibold text-primary-light'>{row.name}</td>
                            <td>{row.code || "-"}</td>
                            <td>{row.level || "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className='text-secondary-light text-xs mb-24'>{t("bulk.field.selectStudents.hint")}</p>
              </>
            )}

            {selectedStudentIds.length === 0 && (
              <div className='alert alert-info mb-0'>{t("bulk.hint.selectAtLeastOne")}</div>
            )}
          </div>
        </div>
      </div>

      {!groupMode && selectedRows.length === 1 && (
        <>
          <div className='col-12'>
            <div className='card radius-12'>
              <div className='card-body'>
                <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-16'>
                  <h6 className='mb-0'>{t("bulk.section.payments")}</h6>
                  <button type='button' className='btn btn-outline-secondary btn-sm' onClick={selectAllSinglePayments}>
                    {t("bulk.action.selectAll")}
                  </button>
                </div>
                <div className='table-responsive'>
                  <table className='table bordered-table mb-0'>
                    <thead>
                      <tr>
                        <th style={{ width: 36 }} />
                        <th scope='col'>{t("propina.col.month")}</th>
                        <th scope='col'>{t("propina.col.propina")}</th>
                        <th scope='col'>{t("propina.col.extras")}</th>
                        <th scope='col'>{t("propina.detail.fine")}</th>
                        <th scope='col'>{t("bulk.col.amountToPay")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {singlePayments.length === 0 && (
                        <tr>
                          <td colSpan={6} className='text-center text-secondary-light py-24'>
                            {t("bulk.hint.noPendingPayments")}
                          </td>
                        </tr>
                      )}
                      {singlePayments.map((p) => {
                        const checked = selectedPayments.includes(p.id);
                        const fine = Number(p.hasFine) === 1 ? parseFloat(p.fine || 0) : 0;
                        const amountToPay =
                          (parseFloat(p.total) || 0) + fine + (payOnlyPropina ? 0 : p.extrasUnpaid || 0);
                        return (
                          <tr key={p.id} role='button' onClick={() => togglePayment(p.id)}>
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type='checkbox'
                                className='form-check-input'
                                checked={checked}
                                onChange={() => togglePayment(p.id)}
                              />
                            </td>
                            <td className='notranslate'>
                              {monthShortName(p.month)}/{p.year}
                            </td>
                            <td>{formatCurrencyMZN(p.total)}</td>
                            <td>{p.extrasUnpaid > 0 ? formatCurrencyMZN(p.extrasUnpaid) : "-"}</td>
                            <td>{fine > 0 ? formatCurrencyMZN(fine) : "-"}</td>
                            <td>
                              <strong>{formatCurrencyMZN(amountToPay)}</strong>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className='col-12'>
            <div className='card radius-12'>
              <div className='card-body'>
                <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-16'>
                  <h6 className='mb-0'>{t("bulk.section.taxes")}</h6>
                  <button type='button' className='btn btn-outline-secondary btn-sm' onClick={selectAllTaxes}>
                    {t("bulk.action.selectAll")}
                  </button>
                </div>
                <div className='table-responsive'>
                  <table className='table bordered-table mb-0'>
                    <thead>
                      <tr>
                        <th style={{ width: 36 }} />
                        <th scope='col'>{t("bulk.col.taxName")}</th>
                        <th scope='col'>{t("propina.extras.amount")}</th>
                        <th scope='col'>{t("bulk.col.taxType")}</th>
                        <th scope='col'>{t("bulk.col.amountToPay")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {availableTaxes.length === 0 && (
                        <tr>
                          <td colSpan={5} className='text-center text-secondary-light py-24'>
                            {t("estudante.list.empty")}
                          </td>
                        </tr>
                      )}
                      {availableTaxes.map((tax) => {
                        const selectedTax = selectedTaxes.find((t2) => t2.taxId === tax.id);
                        const checked = !!selectedTax;
                        const fullValue = taxValueOf(tax);
                        const amountForTax = selectedTax?.amountPaid ?? fullValue;
                        const remainingForTax = Math.max(0, fullValue - (parseFloat(amountForTax) || 0));
                        return (
                          <tr key={tax.id} role='button' onClick={() => toggleTax(tax)}>
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type='checkbox'
                                className='form-check-input'
                                checked={checked}
                                onChange={() => toggleTax(tax)}
                              />
                            </td>
                            <td>{taxNameOf(tax)}</td>
                            <td>{formatCurrencyMZN(fullValue)}</td>
                            <td>{tax.type || tax.periodicity || "-"}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              {checked ? (
                                <>
                                  <input
                                    type='number'
                                    min={0}
                                    max={fullValue}
                                    step='0.01'
                                    className='form-control form-control-sm'
                                    style={{ maxWidth: 140 }}
                                    value={amountForTax}
                                    onChange={(e) => setTaxAmount(tax.id, e.target.value)}
                                  />
                                  {remainingForTax > 0.01 && (
                                    <span className='text-warning-main text-xs d-block mt-4'>
                                      {t("bulk.field.amountPaid.remaining", { value: formatCurrencyMZN(remainingForTax) })}
                                    </span>
                                  )}
                                </>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className='col-12'>
            <div className='card radius-12'>
              <div className='card-body'>
                <h6 className='mb-16'>{t("bulk.section.config")}</h6>
                <div className='row gy-3'>
                  {hasExtrasInSelection && (
                    <div className='col-12'>
                      <label className='form-label'>{t("bulk.field.payOnlyPropina")}</label>
                      <div className='d-flex gap-24'>
                        <div className='form-check'>
                          <input
                            id='payWithExtras'
                            type='radio'
                            className='form-check-input'
                            checked={!payOnlyPropina}
                            onChange={() => setPayOnlyPropina(false)}
                          />
                          <label htmlFor='payWithExtras' className='form-check-label'>
                            {t("bulk.field.payOnlyPropina.with")}
                          </label>
                        </div>
                        <div className='form-check'>
                          <input
                            id='payOnly'
                            type='radio'
                            className='form-check-input'
                            checked={payOnlyPropina}
                            onChange={() => setPayOnlyPropina(true)}
                          />
                          <label htmlFor='payOnly' className='form-check-label'>
                            {t("bulk.field.payOnlyPropina.only")}
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className='col-sm-4'>
                    <label className='form-label'>{t("bulk.field.discountType")}</label>
                    <div className='d-flex gap-16'>
                      <div className='form-check'>
                        <input
                          id='discountPercentage'
                          type='radio'
                          className='form-check-input'
                          checked={discountType === "percentage"}
                          onChange={() => setDiscountType("percentage")}
                        />
                        <label htmlFor='discountPercentage' className='form-check-label'>
                          %
                        </label>
                      </div>
                      <div className='form-check'>
                        <input
                          id='discountFixed'
                          type='radio'
                          className='form-check-input'
                          checked={discountType === "fixed"}
                          onChange={() => setDiscountType("fixed")}
                        />
                        <label htmlFor='discountFixed' className='form-check-label'>
                          MZN
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className='col-sm-4'>
                    <label className='form-label'>{t("bulk.field.discountValue")}</label>
                    <div className="icon-field">
                      <span className="icon">
                        <Icon icon="solar:ticket-sale-outline" />
                      </span>
                      <input
                        type='number'
                        min='0'
                        max={discountType === "percentage" ? 100 : totalOriginal}
                        step='0.01'
                        className='form-control'
                        value={discountValue}
                        onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                  {discountValue > 0 && (
                    <div className='col-sm-4'>
                      <label className='form-label'>{t("bulk.field.discountScope")}</label>
                      <select
                        className='form-control form-select'
                        value={discountScope}
                        onChange={(e) => setDiscountScope(e.target.value)}
                      >
                        {availableScopes.map((s) => (
                          <option key={s.value} value={s.value}>
                            {t(s.key)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className='col-sm-6'>
                    <label className='form-label'>{t("propina.field.paymentMethod")} <span className='required-asterisk'>*</span></label>
                    <select
                      className='form-control form-select'
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                      {paymentMethods.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='col-sm-6'>
                    <label className='form-label'>{t("propina.field.paymentDate")}</label>
                    <div className="icon-field">
                      <span className="icon">
                        <Icon icon="solar:calendar-outline" />
                      </span>
                      <input
                        type='date'
                        max={todayIso()}
                        className='form-control'
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='col-12'>
            <div className='card radius-12'>
              <div className='card-body'>
                <h6 className='mb-16'>{t("bulk.section.summary")}</h6>
                <div className='row gy-3 mb-16'>
                  <div className='col-sm-4'>
                    <div className='p-16 radius-8 bg-primary-50 text-center'>
                      <span className='text-secondary-light text-sm d-block'>{t("bulk.summary.original")}</span>
                      <strong className='text-primary-600 text-xl'>{formatCurrencyMZN(totalOriginal)}</strong>
                    </div>
                  </div>
                  <div className='col-sm-4'>
                    <div className='p-16 radius-8 bg-danger-focus text-center'>
                      <span className='text-secondary-light text-sm d-block'>{t("bulk.summary.discount")}</span>
                      <strong className='text-danger-main text-xl'>{formatCurrencyMZN(discountAmount)}</strong>
                    </div>
                  </div>
                  <div className='col-sm-4'>
                    <div className='p-16 radius-8 bg-success-focus text-center'>
                      <span className='text-secondary-light text-sm d-block'>{t("bulk.summary.final")}</span>
                      <strong className='text-success-main text-xl'>{formatCurrencyMZN(finalTotal)}</strong>
                    </div>
                  </div>
                </div>
                <div className='alert alert-info'>
                  {t("bulk.summary.info", {
                    payments: selectedPayments.length,
                    taxes: selectedTaxes.length,
                    method: paymentMethod,
                  })}
                </div>
                <div className='text-end'>
                  <button type='button' className='btn btn-primary-600 px-32' onClick={handleOpenConfirm}>
                    <i className='ri-check-line' /> {t("bulk.action.confirmPayment", { value: formatCurrencyMZN(finalTotal) })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {groupMode && (
        <>
          <div className='col-12'>
            <div className='card radius-12'>
              <div className='card-body'>
                <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-16'>
                  <h6 className='mb-0'>{t("bulk.section.groupedPayments")}</h6>
                  <button type='button' className='btn btn-outline-secondary btn-sm' onClick={selectAllBuckets}>
                    {t("bulk.action.selectAll")}
                  </button>
                </div>
                <div className='table-responsive'>
                  <table className='table bordered-table mb-0'>
                    <thead>
                      <tr>
                        <th style={{ width: 36 }} />
                        <th scope='col'>{t("bulk.col.students")}</th>
                        <th scope='col'>{t("propina.col.month")}</th>
                        <th scope='col'>{t("propina.col.propina")}</th>
                        <th scope='col'>{t("propina.detail.fine")}</th>
                        <th scope='col'>{t("bulk.col.amountToPay")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedByMonth.length === 0 && (
                        <tr>
                          <td colSpan={6} className='text-center text-secondary-light py-24'>
                            {t("bulk.hint.noPendingPayments")}
                          </td>
                        </tr>
                      )}
                      {groupedByMonth.map((bucket) => {
                        const checked = isBucketSelected(bucket);
                        return (
                          <tr key={bucket.key} role='button' onClick={() => toggleBucket(bucket)}>
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type='checkbox'
                                className='form-check-input'
                                checked={checked}
                                onChange={() => toggleBucket(bucket)}
                              />
                            </td>
                            <td>{bucket.studentNames.join(", ")}</td>
                            <td className='notranslate'>
                              {monthShortName(bucket.month)}/{bucket.year}
                            </td>
                            <td>{formatCurrencyMZN(bucket.total)}</td>
                            <td>{bucket.fine > 0 ? formatCurrencyMZN(bucket.fine) : "-"}</td>
                            <td>
                              <strong>{formatCurrencyMZN(bucket.total + bucket.fine)}</strong>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className='col-12'>
            <div className='card radius-12'>
              <div className='card-body'>
                <h6 className='mb-16'>{t("bulk.section.groupConfig")}</h6>
                <div className='row gy-3'>
                  <div className='col-sm-4'>
                    <label className='form-label'>{t("bulk.field.amountPaid")} <span className='required-asterisk'>*</span></label>
                    <div className="icon-field">
                      <span className="icon">
                        <Icon icon="solar:dollar-minimalistic-outline" />
                      </span>
                      <input
                        type='number'
                        min='0.01'
                        max={groupSelectedTotal}
                        step='0.01'
                        className='form-control'
                        value={amountPaid === "" ? groupSelectedTotal.toFixed(2) : amountPaid}
                        onChange={(e) => setAmountPaid(e.target.value)}
                      />
                    </div>
                    {groupRemaining > 0.01 && (
                      <span className='text-warning-main text-xs'>
                        {t("bulk.field.amountPaid.remaining", { value: formatCurrencyMZN(groupRemaining) })}
                      </span>
                    )}
                  </div>
                  <div className='col-sm-4'>
                    <label className='form-label'>{t("propina.field.paymentMethod")} <span className='required-asterisk'>*</span></label>
                    <select
                      className='form-control form-select'
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                      {paymentMethods.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='col-sm-4'>
                    <label className='form-label'>{t("propina.field.paymentDate")}</label>
                    <div className="icon-field">
                      <span className="icon">
                        <Icon icon="solar:calendar-outline" />
                      </span>
                      <input
                        type='date'
                        max={todayIso()}
                        className='form-control'
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='col-12'>
            <div className='card radius-12'>
              <div className='card-body'>
                <h6 className='mb-16'>{t("bulk.section.summary")}</h6>
                <div className='row gy-3 mb-16'>
                  <div className='col-sm-6'>
                    <div className='p-16 radius-8 bg-primary-50 text-center'>
                      <span className='text-secondary-light text-sm d-block'>{t("bulk.summary.original")}</span>
                      <strong className='text-primary-600 text-xl'>{formatCurrencyMZN(groupSelectedTotal)}</strong>
                    </div>
                  </div>
                  <div className='col-sm-6'>
                    <div className='p-16 radius-8 bg-success-focus text-center'>
                      <span className='text-secondary-light text-sm d-block'>{t("bulk.summary.final")}</span>
                      <strong className='text-success-main text-xl'>{formatCurrencyMZN(groupAmountPaid)}</strong>
                    </div>
                  </div>
                </div>
                <div className='text-end'>
                  <button
                    type='button'
                    className='btn btn-primary-600 px-32'
                    onClick={handleOpenConfirm}
                    disabled={selectedPayments.length < 2 || !paymentMethod}
                  >
                    <i className='ri-check-line' /> {t("bulk.action.confirmPayment", { value: formatCurrencyMZN(groupAmountPaid) })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div
        className={`modal fade ${confirmOpen ? "show d-block" : ""}`}
        tabIndex={-1}
        style={confirmOpen ? { backgroundColor: "rgba(0,0,0,0.5)" } : {}}
      >
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content radius-16 bg-base'>
            <div className='modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0'>
              <h6 className='modal-title mb-0'>{t("bulk.confirm.title")}</h6>
              <button type='button' className='btn-close' onClick={() => setConfirmOpen(false)} />
            </div>
            <div className='modal-body p-24'>
              <table className='table table-bordered table-sm mb-0 bg-base'>
                <tbody>
                  {groupMode ? (
                    <>
                      <tr>
                        <th className='w-50 bg-neutral-50'>{t("bulk.col.students")}</th>
                        <td>{selectedRows.map((r) => r.name).join(", ")}</td>
                      </tr>
                      <tr>
                        <th className='bg-neutral-50'>{t("bulk.summary.original")}</th>
                        <td>{formatCurrencyMZN(groupSelectedTotal)}</td>
                      </tr>
                      <tr>
                        <th className='bg-neutral-50'>{t("bulk.field.amountPaid")}</th>
                        <td>
                          <strong>{formatCurrencyMZN(groupAmountPaid)}</strong>
                        </td>
                      </tr>
                    </>
                  ) : (
                    <>
                      <tr>
                        <th className='w-50 bg-neutral-50'>{t("bulk.section.payments")}</th>
                        <td>{selectedPayments.length}</td>
                      </tr>
                      <tr>
                        <th className='bg-neutral-50'>{t("bulk.section.taxes")}</th>
                        <td>{selectedTaxes.length}</td>
                      </tr>
                      <tr>
                        <th className='bg-neutral-50'>{t("bulk.summary.original")}</th>
                        <td>{formatCurrencyMZN(totalOriginal)}</td>
                      </tr>
                      <tr>
                        <th className='bg-neutral-50'>{t("bulk.summary.discount")}</th>
                        <td>{formatCurrencyMZN(discountAmount)}</td>
                      </tr>
                      <tr>
                        <th className='bg-neutral-50'>{t("bulk.summary.final")}</th>
                        <td>
                          <strong>{formatCurrencyMZN(finalTotal)}</strong>
                        </td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <th className='bg-neutral-50'>{t("propina.field.paymentMethod")}</th>
                    <td>{paymentMethod}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className='modal-footer border border-bottom-0 border-start-0 border-end-0'>
              <button
                type='button'
                className='btn btn-neutral-500 border-neutral-100 px-24'
                onClick={() => setConfirmOpen(false)}
              >
                {t("global.cancel")}
              </button>
              <button
                type='button'
                className='btn btn-primary-600 px-24'
                onClick={handleConfirmSubmit}
                disabled={processingPayment}
              >
                {processingPayment ? "..." : <><i className='ri-check-line' /> {t("global.confirm")}</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacturacaoAgregadaLayer;
